'use client';

import { useEffect, useRef, useState } from 'react';

type UseInViewOptions = {
  /** Stop observing after the first intersection (default true). */
  once?: boolean;
  /** IntersectionObserver `rootMargin`, e.g. `'-10% 0px'`. */
  margin?: string;
  /** IntersectionObserver `threshold` (default `0`). */
  threshold?: number | number[];
};

/**
 * IntersectionObserver-based viewport visibility. Returns a ref to attach to
 * the observed element and whether it is currently in view.
 */
export function useInView<T extends Element = HTMLDivElement>({
  once = true,
  margin = '0px',
  threshold = 0,
}: UseInViewOptions = {}) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        }
      },
      { rootMargin: margin, threshold },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [once, margin, threshold]);

  return { ref, inView };
}
